import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { HOMEOWNERS_INSURANCE_RATE } from '@/utils/calculator/assumptions';
import { estimateStateClosingTaxes } from '@/utils/fees/stateClosingTaxes';
import { CalculateClosingDisclosureProps, calculateTaxFactor } from '.';
import { calculateEnhancedInterestRate } from './rateIntegration';

// Fallback interest rate calculation based on experience (original logic)
const calculateExperienceBasedInterestRate = (experience: number) => {
  let interestRate;
  if (experience === 0 || experience === 1) {
    interestRate = 12.49;
  } else if (experience > 2 && experience < 4) {
    interestRate = 12.24;
  } else if (experience > 3 && experience < 6) {
    interestRate = 11.99;
  } else if (experience > 5 && experience < 8) {
    interestRate = 11.49;
  } else if (experience > 7 && experience < 19) {
    interestRate = 10.49;
  } else {
    interestRate = 9.99;
  }
  return interestRate;
};

export const calculateFixAndFlipClosingDisclosure = (
  {
    offerPrice,
    listPrice,
    downPayment = 0,
    zipCode = 0,
    hoaFees = 0,
    commission = 0,
    monthlyPropertyTaxes: monthlyPropertyTaxesOverride,
    monthlyHomeownersInsurance: monthlyHomeownersInsuranceOverride,
    marketInterestRate = 7.0,
    creditScore = 0,
    experience = 0,
    rehab = 0,
    arv = 0,
    loanTerm = 30,
  }: CalculateClosingDisclosureProps,
  state?: RootState
) => {
  const purchasePrice = offerPrice || listPrice;
  const loanAmount = purchasePrice - downPayment;
  const ltv = loanAmount / purchasePrice;

  const taxFactor = calculateTaxFactor(zipCode);

  // Enhanced interest rate calculation using the interest rate system
  // For FixAndFlip, we use the experience-based rate as a fallback if no state provided
  const interestRate = state
    ? calculateEnhancedInterestRate(
        state,
        'FixAndFlip',
        marketInterestRate,
        creditScore,
        ltv
      )
    : calculateExperienceBasedInterestRate(experience);

  //change on user input on account page
  const numberOfPayments = loanTerm * 12;
  const totalLoanAmount = purchasePrice - downPayment + rehab;

  // constant fees
  const countyRecordingFees = 250;
  const stateRecordingFees = 150;
  const titleTitleSearchBase = 250;
  const recordingDeedBase = 75;
  const appraisalFeeBase = 550;
  const underwritingFee = 1295;
  const applicationFeeBase = 495;
  const recordingTitle = 0; // No separate title recording fee
  const recordingMortgage = 75;
  const surveyFee = 250;
  const pestInspectionFee = 145;
  const floodMonitoringFee = 25;
  const floodDeterminationFee = 35;
  const creditReportFeeBase = 150;
  const processingFeeBase = 795;
  const titleSettlementAgentFee = 650;
  const earnestMoneyDeposit = 1000;
  const insuranceBinder = 150;
  const lenderCredits = loanAmount * 0.01;
  const initialEscrowMortgageInsurance = 0;
  // Tax proration depends on the closing date; no flat credit is assumed.
  const prepaidsPropertyTaxes = 0;

  // factored calculations
  const annualPropertyTax = (purchasePrice * taxFactor) / 100;
  //UNFACTORED CALCULATIONS, NEED TO FIGURE OUT HOW TO INCLUDE CREDIT AND STATE FACTORS
  const optionalOwnersTitleInsuranceBase = purchasePrice * 0.00575; //State, Credit
  const minimumOwnersTitle = 650;
  const maximumOwnersTitle = 3850;
  const optionalOwnersTitleInsurance = Math.min(
    Math.max(optionalOwnersTitleInsuranceBase, minimumOwnersTitle),
    maximumOwnersTitle
  );
  const recordingTransferTaxBase = purchasePrice * 0.01; //State dependent.
  const monthlyHomeownersInsuranceBase =
    (purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12;
  const monthlyHomeownersInsurance =
    monthlyHomeownersInsuranceOverride ?? monthlyHomeownersInsuranceBase;
  const upfrontHomeownersInsurance = purchasePrice * HOMEOWNERS_INSURANCE_RATE;
  const monthlyPropertyTaxesBase = annualPropertyTax / 12;
  const monthlyPropertyTaxes =
    monthlyPropertyTaxesOverride ?? monthlyPropertyTaxesBase;
  const prepaidsHomeownersInsurance =
    ((purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12) * 3;
  // Lender's policy covers the loan amount.
  const titleLendersTitleInsuranceBase = loanAmount * 0.004;
  const homeInspectionFeeBase = purchasePrice * 0.0035;
  const minimumInspectionFee = 650;
  const maximumInspectionFee = 4500;
  const homeInspectionFee = Math.min(
    Math.max(homeInspectionFeeBase, minimumInspectionFee),
    maximumInspectionFee
  );
  const initialEscrowTaxes = monthlyPropertyTaxes * 6; //
  const initialEscrowHOI = ((purchasePrice * 0.01) / 12) * 3;
  const estimatedEscrow = monthlyHomeownersInsurance + monthlyPropertyTaxes;

  //these aren't going to change, they are determined
  const dailyInterestRate = interestRate / 365 / 100; // Convert annual rate to daily decimal
  const prepaidInterest = totalLoanAmount * dailyInterestRate * 15; // Return the result rounded to 2 decimal places
  const monthlyInterestRate = interestRate / 12 / 100; // Convert annual rate to monthly decimal
  const interestPayment = totalLoanAmount * monthlyInterestRate;
  const piti = interestPayment + estimatedEscrow;
  // Buyer's share of the state deed/transfer tax (by state custom) and the
  // borrower's loan taxes — see utils/fees/stateClosingTaxes.
  const stateTaxes = estimateStateClosingTaxes({
    zipCode,
    price: purchasePrice,
    loanAmount,
  });
  const transferTaxes = stateTaxes.transferTaxes;
  const mortgageTaxes = stateTaxes.mortgageTaxes;
  const cleanPayment = piti;
  const titleCosts =
    optionalOwnersTitleInsurance +
    pestInspectionFee +
    insuranceBinder +
    titleTitleSearchBase +
    titleLendersTitleInsuranceBase +
    surveyFee +
    titleSettlementAgentFee;
  const govCosts =
    stateRecordingFees +
    countyRecordingFees +
    recordingMortgage +
    recordingTitle +
    recordingDeedBase +
    transferTaxes +
    mortgageTaxes;
  const prepaidCosts =
    initialEscrowTaxes +
    prepaidsPropertyTaxes +
    prepaidInterest +
    prepaidsHomeownersInsurance;
  const lenderCostTotals =
    floodDeterminationFee +
    floodMonitoringFee +
    creditReportFeeBase +
    appraisalFeeBase +
    applicationFeeBase +
    underwritingFee +
    processingFeeBase;
  const closingCosts =
    titleCosts + lenderCostTotals + govCosts + prepaidCosts - lenderCredits;
  const cashOutsideClosing =
    appraisalFeeBase + earnestMoneyDeposit + homeInspectionFee;
  const cashToClose = closingCosts + downPayment - earnestMoneyDeposit;
  const unbeatableCredit = (purchasePrice * commission) / 100; //this is where we put the buyers agent commission
  const unbeatableDeal = cashToClose - unbeatableCredit;

  const carry = monthlyInterestRate * totalLoanAmount;
  const totalCost = closingCosts + downPayment + totalLoanAmount;
  const profitMargin = arv - totalCost;

  // Perform calculations using the provided purchase price and constants
  // Prepare the closing disclosure object
  const closingDisclosure = {
    type: USER_LOAN_PROGRAM.FIX_AND_FLIP,
    offerPrice,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate,
    creditScore,
    downPayment,
    loanAmount,
    interestRate,
    cleanPayment,
    experience,
    totalLoanAmount,
    processingFeeBase,
    interestPayment,
    homeInspectionFee,
    loanTerm,
    optionalOwnersTitleInsurance,
    hoaFees,
    earnestMoneyDeposit,
    piti,
    estimatedEscrow,
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    prepaidsHomeownersInsurance,
    prepaidsPropertyTaxes,
    prepaidInterest,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    titleTitleSearchBase,
    insuranceBinder,
    recordingDeedBase,
    appraisalFeeBase,
    underwritingFee,
    applicationFeeBase,
    upfrontHomeownersInsurance,
    pestInspectionFee,
    floodMonitoringFee,
    floodDeterminationFee,
    creditReportFeeBase,
    recordingTitle,
    recordingMortgage,
    countyRecordingFees,
    cashOutsideClosing,
    stateRecordingFees,
    surveyFee,
    initialEscrowTaxes,
    initialEscrowMortgageInsurance,
    initialEscrowHOI,
    lenderCredits,
    lenderCostTotals,
    rehab,
    carry,
    titleCosts,
    prepaidCosts,
    govCosts,
    closingCosts,
    // Exactly what closingCosts sums — the fee breakdown itemizes these.
    closingCostParts: {
      optionalOwnersTitleInsurance,
      pestInspectionFee,
      insuranceBinder,
      titleTitleSearchBase,
      titleLendersTitleInsuranceBase,
      surveyFee,
      titleSettlementAgentFee,
      floodDeterminationFee,
      floodMonitoringFee,
      creditReportFeeBase,
      appraisalFeeBase,
      applicationFeeBase,
      underwritingFee,
      processingFeeBase,
      stateRecordingFees,
      countyRecordingFees,
      recordingMortgage,
      recordingTitle,
      recordingDeedBase,
      transferTaxes,
      mortgageTaxes,
      initialEscrowTaxes,
      prepaidsPropertyTaxes,
      prepaidInterest,
      prepaidsHomeownersInsurance,
      lenderCredits: -lenderCredits,
    },
    totalCost,
    cashToClose,
    unbeatableCredit,
    unbeatableDeal,
    transferTaxes,
    mortgageTaxes,
    stateTaxLines: stateTaxes.lines,
    stateTaxState: stateTaxes.state,
    stateTaxNote: stateTaxes.localNote,
    profitMargin,
  };
  return closingDisclosure;
};

export type ProductFixAndFlipEsitmation = ReturnType<
  typeof calculateFixAndFlipClosingDisclosure
>;
